import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PiQrCodeLight } from 'react-icons/pi'
import {
  Typography,
  Button,
  Input,
  Select,
  Option,
  Dialog,
  DialogBody,
  DialogFooter,
  Alert,
} from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import ShortUniqueId from 'short-unique-id'
import RoleChip from '../components/RoleChip'
import { validateEmail } from '../utils/helpers/validate.helpers'
import { sendUpgradeCode } from '../utils/helpers/supabase.helpers'
import { getRoleId, getRoleLabel } from '../utils/helpers/roles.helpers'
import { useGeneralToast } from '../hooks/toast'
import Container from '../components/Container'
import GoBack from '../components/GoBack'
import { useDarkMode } from '../contexts/DarkModeContext'

const { randomUUID } = new ShortUniqueId({ length: 10 })

const GenerateCode: React.FC = () => {
  const { t } = useTranslation()
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const { darkMode } = useDarkMode()
  const [email, setEmail] = useState<string>('')
  const [role, setRole] = useState<string>('')
  const [emailErr, setEmailErr] = useState<boolean>(false)
  const [roleErr, setRoleErr] = useState<boolean>(false)
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [successOp, setSuccessOp] = useState<boolean>(false)
  const [generatedCode, setGeneratedCode] = useState<string>('')

  const onEmailChange = ({ target }: { target: { value: string } }) => {
    setEmailErr(false)
    setEmail(target.value)
  }

  const onRoleChange = (value: string | undefined) => {
    setRoleErr(false)
    setRole(value || '')
  }

  const onSubmit = () => {
    if (!validateEmail(email)) {
      setEmailErr(true)
    }

    if (!role) {
      setRoleErr(true)
    }

    if (validateEmail(email) && role) {
      setShowConfirm(true)
    }
  }

  const onConfirm = async () => {
    setShowConfirm(false)

    setIsLoading(true)

    const code = randomUUID()

    const roleId = getRoleId(role)
    const roleLabel = getRoleLabel(role)

    const { error } = await sendUpgradeCode({
      email,
      role: roleId,
      roleLabel,
      code,
    })

    if (error) {
      toastError((error as { message?: string })?.message ?? JSON.stringify(error))
    } else {
      toastSuccess(t('adminGenerateCode.success.title'))
      setGeneratedCode(code)
      setSuccessOp(true)
    }

    setIsLoading(false)
  }

  return (
    <Container direction="col" className="text-primary">
      <Dialog open={showConfirm} size="xs" handler={() => setShowConfirm(false)}>
        <DialogBody className="text-black">
          {t('adminGenerateCode.confirmDialog.title')} <strong>{email}</strong>{' '}
          {t('adminGenerateCode.confirmDialog.forRole')}{' '}
          <span className="inline-block">
            <RoleChip role={getRoleId(role)} />
          </span>
          ?
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button size="sm" color="red" variant="outlined" onClick={() => setShowConfirm(false)}>
            {t('adminGenerateCode.confirmDialog.cancel')}
          </Button>
          <Button size="sm" color="yellow" onClick={onConfirm}>
            {t('adminGenerateCode.confirmDialog.confirm')}
          </Button>
        </DialogFooter>
      </Dialog>

      <div className="mb-2 self-start">
        <GoBack />
      </div>

      <div className="flex flex-col max-w-lg mx-auto mb-12">
        <Typography variant="lead">{t('adminGenerateCode.title')}</Typography>

        <div className="my-6">
          <Alert color="blue" variant="ghost" icon={<PiQrCodeLight className="h-8 w-8" />}>
            {t('adminGenerateCode.description')}{' '}
            <Link to="/admin/new-user" className="underline">
              {t('adminGenerateCode.clickHere')}
            </Link>{' '}
            {t('adminGenerateCode.toInviteNewUser')}
          </Alert>
        </div>

        <div className="my-2">
          <Input
            type="email"
            label={t('adminGenerateCode.emailLabel')}
            value={email}
            onChange={onEmailChange}
            error={emailErr}
            readOnly={successOp}
            disabled={successOp}
          />

          <Typography style={{ visibility: emailErr ? 'visible' : 'hidden' }} variant="small" color="red">
            {t('adminGenerateCode.insertValidEmail')}
          </Typography>
        </div>

        <div className="my-2">
          <Select
            label={t('adminGenerateCode.planLabel')}
            error={roleErr}
            value={role}
            onChange={onRoleChange}
            disabled={successOp}
            menuProps={{ className: 'dark:bg-primary' }}
            {...(darkMode && { color: 'blue-gray' })}
            labelProps={{
              className:
                'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
            }}
          >
            <Option value="early_bird">
              <RoleChip role={getRoleId('early_bird')} />
            </Option>
            <Option value="content_creator">
              <RoleChip role={getRoleId('content_creator')} />
            </Option>
            <Option value="venue">
              <RoleChip role={getRoleId('venue')} />
            </Option>
          </Select>

          <Typography style={{ visibility: roleErr ? 'visible' : 'hidden' }} variant="small" color="red">
            {t('adminGenerateCode.choosePlan')}
          </Typography>
        </div>

        {!successOp && (
          <div className="my-2">
            <Button color="yellow" size="sm" onClick={onSubmit} disabled={isLoading}>
              {t('adminGenerateCode.sendInvite')}
            </Button>
          </div>
        )}

        {successOp && (
          <div className="my-2">
            <Alert color="green" variant="ghost">
              {t('adminGenerateCode.success.description')}{' '}
              <Link to="/admin/list-codes" className="underline">
                {t('adminGenerateCode.success.thisSection')}
              </Link>{' '}
              {t('adminGenerateCode.success.details')}
              <ul className="mt-2 ml-2 list-inside list-disc">
                <li>
                  {t('adminGenerateCode.success.user')}
                  <span className="px-1 font-bold">{email}</span>
                </li>
                <li>
                  {t('adminGenerateCode.success.role')}
                  <span className="px-1 font-bold">{role}</span>
                </li>
                <li>
                  {t('adminGenerateCode.success.code')}
                  <span className="px-1 font-bold">{generatedCode}</span>
                </li>
              </ul>
            </Alert>
          </div>
        )}
      </div>
    </Container>
  )
}

export default GenerateCode
